
import Stepper from 'primevue/stepper';
import StepList from 'primevue/steplist';
import StepPanels from 'primevue/steppanels';
import StepItem from 'primevue/stepitem';
import Step from 'primevue/step';
import StepPanel from 'primevue/steppanel';

import IftaLabel from 'primevue/iftalabel';
import InputNumber from 'primevue/inputnumber';

import Card from 'primevue/card';
import Button from 'primevue/button';
import ProgressSpinner from 'primevue/progressspinner';
import Toast from 'primevue/toast';

import { computed, defineComponent, onMounted, ref, watch } from 'vue';
import { useAuthStore } from '../../stores/auth';
import { storeToRefs } from 'pinia';
import { depositWithdraw } from '../../apis/deposit_withdraw';
import { CreateDecimalDepositTransResult, DecimalDepositInfoResult } from '../../types/deposit_withdraw';
import BankDetail from '../card/BankDetail.vue';
import { useWebStore } from '../../stores/web';
import { useToast } from 'primevue/usetoast';
import { showVuePopup } from '../../utils/showVuePopup';
import WalletUpdate from '../../popup/WalletUpdate.vue';
import { useSession } from '../../stores/session';

export default defineComponent({
	name: 'DepositDecimal',
	components: {
		BankDetail,
		Stepper,
		StepList,
		StepPanels,
		StepItem,
		Step,
		StepPanel,
		IftaLabel,
		InputNumber,
		Card,
		Button,
		ProgressSpinner,
		Toast,
	},
	setup() {
		const auth = useAuthStore();
		const { profile } = storeToRefs(auth);
		auth.hydrate();

		const web = useWebStore();
		web.hydrate();
		const { webPermission } = storeToRefs(web);

		const toast = useToast();
		const session = useSession();

		onMounted(() => {
			auth.hydrate();
			web.hydrate();
			handleDecimalDepositInfo();
		});

		const stepper = ref<string>('1');

		// เปิด/ปิดช่องทาง (reactive)
		const isActive = computed<boolean>(() => webPermission.value?.isDeposit?.isDecimal ?? false);

		const isBankUpdate = computed(() => !(!!profile.value?.customerBankAccount && !!profile.value?.customerBankName));

		const form = ref({
			amount: 0,
			btn: { disabled: false, loading: false },
		});

		const amountLimit = ref<{ lists: string[]; max: number }>({
			lists: [],
			max: 0,
		});

		const bankDeposit = ref({
			data: {
				fullName: '',
				bankName: '',
				bankNumber: '',
				bankCode: '',
				amount: 0,
				expireSeconds: 0, // ← ใช้ seconds ที่เหลือ
			},
			active: false,
			loading: false,
		});

		const handleDecimalDepositInfo = async () => {
			try {
				const resp = await depositWithdraw.getDecimalDepositInfo({ data: null });
				if (resp.code === '8017') {
					alert(resp.message);
					await auth.logout();
					window.location.reload();
					return;
				}
				if (resp.code !== '0') throw new Error(resp.message);

				const result = resp.result as DecimalDepositInfoResult;

				amountLimit.value = {
					lists: result.decimalShortCutAmount || [],
					max: result.maxDepositDecimal || 0,
				};
			} catch (error: any) {
				toast.add({ severity: 'error', summary: 'เกิดข้อผิดพลาดบางอย่าง', detail: error?.message || String(error), life: 3000 });
			}
		};

		const handleDecimalDepositCreate = async (payload: { amount: number }) => {
			bankDeposit.value.loading = true;
			try {
				const resp = await depositWithdraw.createDecimalDepositTrans(payload);

				if (resp.code === '8017') {
					alert(resp.message);
					await auth.logout();
					window.location.reload();
					return;
				}
				if (resp.code !== '0') throw new Error(resp.message);

				const result = resp.result as CreateDecimalDepositTransResult;

				bankDeposit.value.data = {
					fullName: result.accountName,
					bankName: result.bankName,
					bankNumber: result.accountNumber,
					bankCode: result.bankName,
					amount: result.amount || 0,
					expireSeconds: parseInt(result.expireDate) || 180,
				};
				bankDeposit.value.active = true;

				stepper.value = '2';
			} catch (error: any) {
				toast.add({ severity: 'error', summary: 'เกิดข้อผิดพลาดบางอย่าง', detail: error?.message || String(error), life: 3000 });
			} finally {
				bankDeposit.value.loading = false;
			}
		};

		const handleDecimalDepositTrans = async () => {
			bankDeposit.value.loading = true;
			try {
				const resp = await depositWithdraw.getDecimalDepositTrans({ data: null });

				if (resp.code === '8017') {
					alert(resp.message);
					await auth.logout();
					window.location.reload();
					return;
				}
				if (resp.code !== '0') throw new Error(resp.message);

				const result = resp.result as CreateDecimalDepositTransResult;

				bankDeposit.value.data = {
					fullName: result.accountName,
					bankName: result.bankName,
					bankNumber: result.accountNumber,
					bankCode: result.bankName,
					amount: result.amount || 0,
					expireSeconds: parseInt(result.expireDate) || 180,
				};
				bankDeposit.value.active = true;
			} catch (error: any) {
				toast.add({ severity: 'error', summary: 'เกิดข้อผิดพลาดบางอย่าง', detail: error?.message || String(error), life: 3000 });
			} finally {
				bankDeposit.value.loading = false;
			}
		};

		// ไป step 2 แล้ว ดึงทรานแซกชันล่าสุด (ถ้ามี)
		watch(stepper, (v) => {
			if (v === '1') {
				handleDecimalDepositInfo();
			}
			if (v === '2') {
				handleDecimalDepositTrans();
			}
		});

		// submit
		const handleSubmit = async () => {
			form.value.btn.loading = true;

			if (form.value.amount <= 0 || form.value.amount > amountLimit.value.max) {
				form.value.btn.loading = false;
				return;
			}

			try {
				await handleDecimalDepositCreate({ amount: form.value.amount });
			} catch (error: any) {
				toast.add({ severity: 'error', summary: 'เกิดข้อผิดพลาดบางอย่าง', detail: error?.message || String(error), life: 3000 });
			} finally {
				form.value.btn.loading = false;
			}
		};

		const addAmountInput = (amount: string) => {
			form.value.amount = parseInt(amount);
		};

		const btnClickUpdateBank = async () => {
			session.set('navbar_primary', 'wallet');
			session.set('navbar_sub', 'wallet:update');
			await showVuePopup(WalletUpdate, { tabs: 'bank' });
		};

		// เมื่อหมดเวลา
		const onExpired = () => {
			bankDeposit.value.active = false;
			toast.add({ severity: 'warn', summary: 'หมดเวลา', detail: 'รายการหมดเวลา กรุณาสร้างใหม่', life: 3000 });
			stepper.value = '1';
		};

		return {
			profile,
			stepper,
			form,
			bankDeposit,
			handleSubmit,
			amountLimit,
			addAmountInput,
			isActive,
			isBankUpdate,
			btnClickUpdateBank,
			onExpired,
		};
	},
});
